//#region import libraries
import React, { useCallback, useEffect, useRef } from 'react';

// drag and drop
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// styling
import { Colors } from '_styles';

//icons
import { FaPlus } from 'react-icons/fa6';

// custom components
import QuestionCard from './question-card';
import { createEmptyQuestion, isQuestionBlank, newQuestionKey } from './survey-validation';

//#endregion

/**
 * Reorderable list of question cards for the survey builder.
 *
 * @param questions        draft questions (each has a client-only `_key`)
 * @param setQuestions     (questions) => void
 * @param languages        [{ label, value }]
 * @param questionErrors   { [_key]: { messages, missingLangs } }
 * @param showErrors       surface validation inline
 * @param expandedKeys     Set of expanded question keys
 * @param setExpandedKeys  (Set) => void
 */
const QuestionForm = ({ questions, setQuestions, languages, questionErrors, showErrors, expandedKeys, setExpandedKeys }) => {

    // Latest values for the stable callbacks below (keeps QuestionCard memoisation effective).
    const questionsRef = useRef(questions);
    useEffect(() => { questionsRef.current = questions; }, [questions]);

    const toggle = useCallback((key) => {
        setExpandedKeys((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    }, [setExpandedKeys]);

    const updateQuestion = useCallback((key, question) => {
        setQuestions((prev) => prev.map((q) => (q._key === key ? question : q)));
    }, [setQuestions]);

    const addQuestion = () => {
        const q = createEmptyQuestion(languages);
        setQuestions([...questions, q]);
        // Collapse finished questions so the new one is the focus.
        const next = new Set([...expandedKeys].filter((k) => {
            const existing = questions.find((x) => x._key === k);
            return existing && (isQuestionBlank(existing) || questionErrors[k]);
        }));
        next.add(q._key);
        setExpandedKeys(next);
        setTimeout(() => document.getElementById(`question-card-${q._key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
    };

    const duplicateQuestion = useCallback((key) => {
        const copyKey = newQuestionKey();
        setQuestions((prev) => {
            const index = prev.findIndex((q) => q._key === key);
            if (index < 0) return prev;
            const next = [...prev];
            next.splice(index + 1, 0, { ...JSON.parse(JSON.stringify(prev[index])), _key: copyKey });
            return next;
        });
        setExpandedKeys((prev) => new Set([...prev, copyKey]));
    }, [setQuestions, setExpandedKeys]);

    const deleteQuestion = useCallback((key) => {
        const current = questionsRef.current;
        const index = current.findIndex((q) => q._key === key);
        if (index < 0) return;
        if (!isQuestionBlank(current[index]) && !window.confirm(`Delete question ${index + 1}?`)) return;
        setQuestions((prev) => prev.filter((q) => q._key !== key));
    }, [setQuestions]);

    const onDragEnd = ({ source, destination }) => {
        if (!destination || destination.index === source.index) return;
        const next = [...questions];
        const [moved] = next.splice(source.index, 1);
        next.splice(destination.index, 0, moved);
        setQuestions(next);
    };

    return (
        <div>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="survey-questions">
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            {questions.map((question, index) => (
                                <Draggable key={question._key} draggableId={question._key} index={index}>
                                    {(dragProvided) => (
                                        <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                                            <QuestionCard
                                                question={question}
                                                index={index}
                                                languages={languages}
                                                expanded={expandedKeys.has(question._key)}
                                                onToggle={toggle}
                                                onChange={updateQuestion}
                                                onDuplicate={duplicateQuestion}
                                                onDelete={deleteQuestion}
                                                canDelete={questions.length > 1}
                                                validation={questionErrors[question._key]}
                                                showErrors={showErrors}
                                                dragHandleProps={dragProvided.dragHandleProps}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <button
                type="button"
                onClick={addQuestion}
                style={{
                    width: '100%', padding: '14px 0', borderRadius: 10, cursor: 'pointer',
                    border: `2px dashed ${Colors.PRM_LIGHT_BLUE}`, background: Colors.PRM_BLUE_ACCENT,
                    color: Colors.PRM_LIGHT_BLUE, fontWeight: 600,
                }}
            >
                <FaPlus style={{ marginRight: 8 }} /> Add question
            </button>
        </div>
    );
};


export default QuestionForm;
